// File: typescript/apps/front-end/src/main/react/Dialog/useAuthoredPostsSearch.ts
//
// The debounced "search the member's posts as they type" state, extracted from
// `SeriesAddDiscussionToSeriesDialog` (FI-594, work order `1a` A8) so the composer's link panel and Embed
// dialog search the same way instead of each growing a copy. The caller supplies the read: the series
// dialog asks its three per-source chooser endpoints, the composer asks the series-free
// `GET /ajax/user/me/discussions/search` (`ClientFactory.searchOwnDiscussions`).
//
// Contract, unchanged from the series dialog it came out of:
//   - `results` is null while there is no query OR while one is in flight, so a stale page never
//     flashes under a new query; an empty array is a real answer.
//   - the read fires once per settled query (250 ms), and a superseded response is dropped.
//   - a failure lands on the caller's definite fallback, never an endless spinner.
// `search` must be referentially stable (wrap it in useCallback): it is an effect dependency.
import React from "react";

export const AUTHORED_POSTS_SEARCH_DEBOUNCE_MS = 250;

export function useAuthoredPostsSearch<T>(
    query: string,
    search: ((trimmedQuery: string) => Promise<T>) | null,
    onError: (error: unknown) => T,
): {
    trimmedQuery: string;
    results: T | null;
    setResults: React.Dispatch<React.SetStateAction<T | null>>;
} {
    const trimmedQuery = React.useMemo(() => query.trim(), [query]);
    const [results, setResults] = React.useState<T | null>(null);
    // Read through a ref: a caller's inline fallback must not restart the debounce on every render.
    const onErrorRef = React.useRef(onError);
    onErrorRef.current = onError;

    React.useEffect(() => {
        if (!trimmedQuery || !search) {
            setResults(null);
            return;
        }

        let active = true;
        setResults(null);
        const handle = window.setTimeout(() => {
            search(trimmedQuery)
                .then((next) => {
                    if (active) {
                        setResults(next);
                    }
                })
                .catch((error) => {
                    if (active) {
                        setResults(onErrorRef.current(error));
                    }
                });
        }, AUTHORED_POSTS_SEARCH_DEBOUNCE_MS);

        return () => {
            active = false;
            window.clearTimeout(handle);
        };
    }, [trimmedQuery, search]);

    return { trimmedQuery, results, setResults };
}
